import { useEffect, useMemo } from 'react';
import { Outlet } from 'react-router-dom';

import { PageTitles } from '@revelio/core';

import { DashboardErrorBoundary } from '../CustomDash/components/DashboardErrorBoundary';
import DashboardPage from '../DashboardPage';
import { useDataBuilderQuery } from './api/useDataBuilderQuery';
import { CompanySelectionGateProvider } from './CompanySelectionGateContext';
import { useDataBuilderSelectionLists } from './components/custom-filters/useDataBuilderSelectionLists';
import { DataBuilderProgressSidebar } from './components/DataBuilderProgressSidebar';
import { DataBuilderPageTemplate } from './components/ui/DataBuilderPageTemplate';
import {
  dataBuilderStore,
  selectDatasetInstances,
  useDataBuilderSelector,
} from './data-builder.store';
import { DataBuilderPaths } from './steps/data-builder-steps';
import { useDataBuilderNavigation } from './useDataBuilderNavigation';
import { usePersistErrorToast } from './usePersistErrorToast';

export { DataBuilderPaths };

/** Collect every selection list key referenced by the API config so we can prefetch them. */
function collectAllSelectionListKeys(
  deliverablesConfig: Record<
    string,
    {
      unitOfAnalysis: { options: { selectionListKeys: string[] }[] };
      generalFilters: {
        customFilterDefinitions: { type: string; keys?: string[] }[];
      };
      breakdowns: {
        customFilterDefinitions: { type: string; keys?: string[] }[];
        customColumnFilterDefinitions: { type: string; keys?: string[] }[];
      }[];
    }
  >
): string[] {
  const keys = new Set<string>();

  for (const config of Object.values(deliverablesConfig)) {
    for (const option of config.unitOfAnalysis.options) {
      for (const key of option.selectionListKeys) {
        keys.add(key);
      }
    }
    for (const def of config.generalFilters.customFilterDefinitions) {
      if (def.type === 'selection_list' && def.keys) {
        for (const key of def.keys) keys.add(key);
      }
    }
    for (const breakdown of config.breakdowns) {
      for (const def of breakdown.customFilterDefinitions) {
        if (def.type === 'selection_list' && def.keys) {
          for (const key of def.keys) keys.add(key);
        }
      }
      for (const def of breakdown.customColumnFilterDefinitions) {
        if (def.type === 'selection_list' && def.keys) {
          for (const key of def.keys) keys.add(key);
        }
      }
    }
  }

  return Array.from(keys);
}

export const DataBuilder = () => {
  const { safeStepIndex, isChooseStep, goToChooseStep } =
    useDataBuilderNavigation();
  const instances = useDataBuilderSelector(selectDatasetInstances);
  const { data } = useDataBuilderQuery();
  usePersistErrorToast();

  // Prefetch all selection lists from the API config so labels resolve
  // immediately when the user navigates to Step 2 or opens a filter modal.
  const allSelectionListKeys = useMemo(
    () =>
      data?.deliverablesConfig
        ? collectAllSelectionListKeys(data.deliverablesConfig)
        : [],
    [data?.deliverablesConfig]
  );
  useDataBuilderSelectionLists({ apiKeys: allSelectionListKeys });

  useEffect(() => {
    return () => {
      dataBuilderStore.getState().reset();
    };
  }, []);

  useEffect(() => {
    if (safeStepIndex > 0 && instances.length === 0) {
      goToChooseStep();
    }
  }, [safeStepIndex, instances.length, goToChooseStep]);

  return (
    <DashboardErrorBoundary context="DataBuilder">
      <DashboardPage
        title={[PageTitles.DELIVERABLES, PageTitles.DATA_BUILDER]}
        loading={false}
      >
        <CompanySelectionGateProvider>
          <DataBuilderPageTemplate
            sidebar={
              isChooseStep ? undefined : (
                <DataBuilderProgressSidebar currentStepIndex={safeStepIndex} />
              )
            }
          >
            <Outlet />
          </DataBuilderPageTemplate>
        </CompanySelectionGateProvider>
      </DashboardPage>
    </DashboardErrorBoundary>
  );
};
